import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { animationsActives } from "@/lib/motionSafe";
import { useSEO, BASE } from "@/lib/seo";
import { IMAGES } from "@/lib/images";
import { METIERS } from "@/lib/partenaires";
import ImageCredit from "@/components/site/ImageCredit";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";

const SOMMAIRE = [
  {
    title: "Comprendre",
    links: [
      { to: "/definition-sculpteur-contemporain", label: "Qu'est-ce qu'un sculpteur contemporain ?" },
      { to: "/courants-et-mouvements", label: "Courants et mouvements de la sculpture contemporaine" },
    ],
  },
  {
    title: "Matériaux et techniques",
    links: [
      { to: "/materiaux-et-techniques", label: "Les matériaux de la sculpture" },
      { to: "/materiaux-et-techniques/bronze", label: "Sculpture en bronze" },
      { to: "/materiaux-et-techniques/resine", label: "Sculpture en résine" },
      { to: "/materiaux-et-techniques/platre", label: "Sculpture en plâtre" },
      { to: "/materiaux-et-techniques/metal", label: "Sculpture en métal" },
      { to: "/materiaux-et-techniques/bois", label: "Sculpture sur bois" },
      { to: "/materiaux-et-techniques/pierre", label: "Sculpture en pierre et en marbre" },
      { to: "/materiaux-et-techniques/terre", label: "Sculpture en argile et terre cuite" },
    ],
  },
  {
    title: "Lexique",
    links: [
      { to: "/lexique-sculpture", label: "Lexique de la sculpture" },
      { to: "/lexique-sculpture/bas-relief", label: "Bas-relief" },
      { to: "/lexique-sculpture/ronde-bosse", label: "Ronde-bosse" },
      { to: "/lexique-sculpture/cire-perdue", label: "Fonte à la cire perdue" },
      { to: "/lexique-sculpture/patine", label: "Patine" },
    ],
  },
  {
    title: "Acheter une sculpture",
    links: [
      { to: "/guide-achat-direct-artiste", label: "Acheter une sculpture en direct de l'atelier" },
      { to: "/faq", label: "Questions fréquentes" },
    ],
  },
  {
    title: "Métiers d'art",
    links: [
      { to: "/partenaires-metiers-d-art", label: "Annuaire des artisans et ateliers d'art" },
      { to: "/devenez-partenaire-metiers-d-art", label: "Inscription gratuite à l'annuaire" },
    ],
  },
];

const CHAPTERS = [
  {
    num: "01",
    title: "Définir",
    text: "Qu'est-ce qu'un sculpteur contemporain français ? Positionnement historique, formation, et ce qui le distingue de la sculpture classique.",
    to: "/definition-sculpteur-contemporain",
    testId: "chapter-definition",
  },
  {
    num: "02",
    title: "Explorer",
    text: "Figuratif, abstraction, pop sculpture, art urbain, monumental : cartographier les courants qui traversent la création actuelle.",
    to: "/courants-et-mouvements",
    testId: "chapter-courants",
  },
  {
    num: "03",
    title: "Comprendre",
    text: "Bronze, argile, résine, marbre, bois, métal : les matériaux, les gestes et les techniques qui donnent corps à l'œuvre.",
    to: "/materiaux-et-techniques",
    testId: "chapter-materiaux",
  },
  {
    num: "04",
    title: "Acquérir",
    text: "Acheter une sculpture en direct d'un atelier plutôt qu'en galerie : repères, certificat d'authenticité, livraison.",
    to: "/guide-achat-direct-artiste",
    testId: "chapter-guide",
  },
];

const COURANTS = [
  {
    title: "Figuratif contemporain",
    text: "Le corps humain reste un territoire inépuisable, réinventé par chaque génération.",
    img: IMAGES.figuratifMaillol,
  },
  {
    title: "Abstraction",
    text: "La forme pour elle-même : volumes, tensions et matière libérés du modèle.",
    img: IMAGES.abstractionCycladique,
  },
  {
    title: "L'œuvre exposée",
    text: "De l'atelier au musée, la sculpture contemporaine dialogue avec l'espace public.",
    img: IMAGES.oeuvreExposee,
  },
];

const LEXIQUE = [
  { term: "Patine", slug: "patine", def: "Coloration de surface appliquée à chaud ou à froid sur un bronze." },
  { term: "Cire perdue", slug: "cire-perdue", def: "Technique de fonte où le modèle en cire disparaît dans le moule." },
  { term: "Épreuve d'artiste", slug: "epreuve-artiste", def: "Exemplaire supplémentaire, marqué EA, hors du tirage numéroté." },
  { term: "Taille directe", slug: "taille-directe", def: "Sculpture par enlèvement de matière, sans modèle intermédiaire." },
  { term: "Ronde-bosse", slug: "ronde-bosse", def: "Œuvre en trois dimensions, dégagée de tout support de fond." },
  { term: "Tirage limité", slug: "tirage-limite", def: "En France, 12 exemplaires maximum pour une œuvre originale." },
];

export default function Home() {
  useSEO(
    "Sculpteur Français — Le Guide Éditorial de la Sculpture Contemporaine",
    "Explorez l'univers de la sculpture contemporaine française : courants artistiques, matériaux nobles, lexique des termes et guide d'achat en direct auprès d'un artiste.",
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Sculpteur Français",
      url: `${BASE}`,
      inLanguage: "fr-FR",
      description:
        "Ressource éditoriale de référence sur la sculpture contemporaine française : définitions, courants, matériaux, lexique et guide d'achat en direct.",
    }
  );

  const heroRef = useRef(null);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#sommaire") {
      document.getElementById("sommaire")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);
  const animOn = animationsActives();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const ringRotate = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <main data-testid="home-page" className="bg-paper">
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative mx-auto grid max-w-7xl gap-12 px-6 pb-20 pt-32 sm:pt-40 lg:grid-cols-12 lg:px-10 lg:pb-28"
      >
        <div className="lg:col-span-7">
          <motion.p
            initial={animOn ? { opacity: 0 } : false}
            animate={animOn ? { opacity: 1 } : undefined}
            transition={{ duration: 1, delay: 0.15 }}
            className="eyebrow"
            data-testid="hero-eyebrow"
          >
            Ressource éditoriale indépendante — sculpture contemporaine en France
          </motion.p>

          <h1 className="mt-8 font-serif text-5xl font-light leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.25}>Comprendre la </MaskedLine>
            <MaskedLine delay={0.37}>
              sculpture <em className="text-bronze">contemporaine</em>{" "}
            </MaskedLine>
            <MaskedLine delay={0.49}>française.</MaskedLine>
          </h1>

          <motion.p
            initial={animOn ? { opacity: 0, y: 18 } : false}
            animate={animOn ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-lg font-light leading-relaxed text-smoke"
          >
            Définitions, courants, matériaux et repères pratiques, sans
            discours commercial ni jargon inutile : Sculpteur Français, la
            référence pour comprendre la sculpture contemporaine et découvrir
            les artisans des métiers d'art.
          </motion.p>

          <motion.div
            initial={animOn ? { opacity: 0, y: 18 } : false}
            animate={animOn ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/definition-sculpteur-contemporain"
              data-testid="hero-cta-definition"
              className="group inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
            >
              Commencer par la définition
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/lexique-sculpture"
              data-testid="hero-cta-lexique"
              className="link-underline inline-flex items-center gap-2 px-1 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-ink"
            >
              Consulter le lexique
            </Link>
          </motion.div>

          <motion.div
            initial={animOn ? { opacity: 0 } : false}
            animate={animOn ? { opacity: 1 } : undefined}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-3 border-t border-ink/10 pt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke"
            data-testid="hero-stats"
          >
            <Link
              to="/courants-et-mouvements"
              data-testid="hero-stat-courants"
              className="transition-colors duration-300 hover:text-bronze"
            >
              6 courants cartographiés
            </Link>
            <Link
              to="/lexique-sculpture"
              data-testid="hero-stat-lexique"
              className="transition-colors duration-300 hover:text-bronze"
            >
              28 termes définis
            </Link>
            <Link
              to="/guide-achat-direct-artiste"
              data-testid="hero-stat-guide"
              className="transition-colors duration-300 hover:text-bronze"
            >
              1 guide d'acquisition
            </Link>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={animOn ? { opacity: 0, scale: 0.96 } : false}
            animate={animOn ? { opacity: 1, scale: 1 } : undefined}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              style={{ rotate: ringRotate }}
              className="absolute -right-8 -top-8 hidden h-40 w-40 rounded-full border border-bronze/40 lg:block"
              aria-hidden="true"
            >
              <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-bronze" />
            </motion.div>
            <div className="img-content-wrap">
              <img
                src={IMAGES.hero.src}
                alt={IMAGES.hero.alt}
                className="img-content border border-ink/15"
                loading="eager"
              />
            </div>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
              {IMAGES.hero.caption}
              <ImageCredit image={IMAGES.hero} testid="credit-hero" />
            </p>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section data-testid="annuaire-banner" className="border-b border-ink/10 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-serif text-3xl font-normal leading-snug tracking-tight text-ink sm:text-4xl">
              Artisans et ateliers d'art : votre fiche gratuite dans l'annuaire des métiers d'art
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {METIERS.map((m) =>
              m.partners.map((p) => (
                <Reveal key={p.id} className="h-full">
                  <Link
                    to={p.detailPage ? `/partenaires/${m.slug}/${p.id}` : `/partenaires/${m.slug}`}
                    data-testid={`annuaire-home-${p.id}`}
                    className="group flex h-full flex-col border border-ink/15 p-7 transition-colors duration-300 hover:border-bronze"
                  >
                    <p className="eyebrow">{m.name}</p>
                    <div className="mt-4 flex items-center gap-5">
                      {p.logoCard && (
                        <img
                          src={p.logoCard.src}
                          alt={p.logoCard.alt}
                          loading="lazy"
                          className="w-20 shrink-0 border border-ink/10 bg-ink"
                          data-testid={`annuaire-home-logo-${p.id}`}
                        />
                      )}
                      <h3 className="font-serif text-2xl font-normal text-ink transition-colors duration-300 group-hover:text-bronze">
                        {p.name}
                      </h3>
                    </div>
                    <p className="mt-4 flex-1 text-sm font-light leading-relaxed text-smoke">{p.blurb}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-bronze">
                      Découvrir la fiche
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              ))
            )}
          </div>
          <Reveal delay={0.1} className="mt-8">
            <Link
              to="/devenez-partenaire-metiers-d-art"
              data-testid="annuaire-banner-btn"
              className="group inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
            >
              Inscrire gratuitement mon atelier
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section id="sommaire" data-testid="sommaire" className="border-b border-ink/10 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl">
              Le guide de la sculpture contemporaine : sommaire
            </h2>
          </Reveal>
          <Reveal delay={0.08} className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {SOMMAIRE.map((g) => (
              <div key={g.title}>
                <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-bronze">{g.title}</h3>
                <ul className="mt-4 space-y-2.5 border-t border-ink/10 pt-4">
                  {g.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="link-underline text-sm font-light text-body transition-colors hover:text-bronze"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section data-testid="manifesto-chapters" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <Reveal>
          <p className="eyebrow">Le sommaire</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-normal leading-snug tracking-tight text-ink sm:text-4xl">
            Quatre chapitres pour entrer dans le sujet
          </h2>
        </Reveal>
        <div className="mt-14 divide-y divide-ink/10 border-y border-ink/10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.08}>
              <Link
                to={c.to}
                data-testid={c.testId}
                className="group grid gap-4 py-8 transition-colors duration-300 hover:bg-cream sm:grid-cols-12 sm:items-center sm:gap-8 sm:px-6"
              >
                <span className="font-mono text-sm text-bronze sm:col-span-1">{c.num}</span>
                <span className="font-serif text-3xl text-ink transition-colors duration-300 group-hover:text-bronze sm:col-span-3 sm:text-4xl">
                  {c.title}
                </span>
                <span className="text-sm font-light leading-relaxed text-smoke sm:col-span-7">
                  {c.text}
                </span>
                <ArrowRight className="h-5 w-5 text-bronze transition-transform duration-300 group-hover:translate-x-2 sm:col-span-1" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="courants-preview" className="bg-cream py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Panorama</p>
              <h2 className="mt-4 font-serif text-3xl font-normal leading-snug tracking-tight text-ink sm:text-4xl">
                Les visages de la sculpture actuelle
              </h2>
            </div>
            <Link
              to="/courants-et-mouvements"
              data-testid="courants-preview-link"
              className="link-underline font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
            >
              Tous les courants
            </Link>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {COURANTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <Link to="/courants-et-mouvements" className="group block" data-testid={`courant-card-${i}`}>
                  <div className="courant-frame border border-ink/15">
                    <img
                      src={c.img.src}
                      alt={c.img.alt}
                      loading="lazy"
                      className="courant-frame-img"
                    />
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-ink transition-colors duration-300 group-hover:text-bronze">
                    {c.title}
                  </h3>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                    {c.img.caption}
                  </p>
                  <p className="mt-2 text-sm font-light leading-relaxed text-smoke">{c.text}</p>
                </Link>
                {c.img.credit && (
                  <p className="mt-2 font-mono text-[10px] tracking-wide text-smoke/80">
                    <ImageCredit image={c.img} testid={`credit-courant-card-${i}`} />
                  </p>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="lexique-preview" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Le lexique</p>
            <h2 className="mt-4 font-serif text-3xl font-normal leading-snug tracking-tight text-ink sm:text-4xl">
              Les mots de la sculpture, expliqués simplement
            </h2>
          </div>
          <Link
            to="/lexique-sculpture"
            data-testid="lexique-preview-link"
            className="link-underline font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
          >
            Lexique complet
          </Link>
        </Reveal>
        <div className="mt-14 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-3">
          {LEXIQUE.map((t, i) => (
            <Reveal key={t.term} delay={i * 0.05} className="bg-paper">
              <Link
                to={`/lexique-sculpture/${t.slug}`}
                data-testid={`home-lexique-${t.slug}`}
                className="group block h-full p-8 transition-colors duration-300 hover:bg-cream"
              >
                <h3 className="font-serif text-2xl text-ink transition-colors group-hover:text-bronze">{t.term}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-smoke">{t.def}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="guide-snippet" className="border-t border-ink/10 bg-ink py-24 text-paper lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow !text-terracotta">Le guide pratique</p>
            <h2 className="mt-4 font-serif text-3xl font-normal leading-snug tracking-tight sm:text-4xl">
              Acheter une sculpture en direct de l'atelier
            </h2>
            <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-paper/60">
              Contacter un sculpteur, visiter son atelier, vérifier un certificat
              d'authenticité, comprendre le prix d'une œuvre : un parcours
              pédagogique, étape par étape, pour acquérir en connaissance de
              cause — sans intermédiaire imposé.
            </p>
            <Link
              to="/guide-achat-direct-artiste"
              data-testid="guide-snippet-cta"
              className="group mt-8 inline-flex items-center gap-3 border border-paper/30 px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] transition-colors duration-300 hover:border-terracotta hover:bg-terracotta hover:text-ink"
            >
              Lire le guide
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="img-content-wrap">
              <img
                src={IMAGES.atelierAnonyme.src}
                alt={IMAGES.atelierAnonyme.alt}
                loading="lazy"
                className="img-content border border-paper/20"
              />
            </div>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-paper/40">
              {IMAGES.atelierAnonyme.caption}
              <ImageCredit image={IMAGES.atelierAnonyme} testid="credit-modelage" />
            </p>
          </Reveal>
        </div>
      </section>

    </main>
  );
}
